import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, X } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { ActionButton } from '@/shared/ui/action-button';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

import { useCameraCapture } from '../model/use-camera-capture';

interface CameraCaptureProps {
  onCapture: (file: File) => void;
  onClose: () => void;
  className?: string;
}

/**
 * Live preview with a shutter, shown in place of the drop zone.
 *
 * The camera is asked for as soon as this mounts, so the button that reveals it
 * is the only click between wanting a photo and the permission prompt. The
 * preview keeps the drop zone's footprint, which stops the form below it from
 * jumping while the camera warms up.
 */
export function CameraCapture({ onCapture, onClose, className }: CameraCaptureProps) {
  const { t } = useTranslation();
  const { status, videoRef, start, capture, stop } = useCameraCapture({ onCapture });

  useEffect(() => {
    void start();
  }, [start]);

  const handleClose = () => {
    stop();
    onClose();
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="relative overflow-hidden rounded-lg border bg-black">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          aria-label={t('gen.take.photo.label', 'Take Photo')}
          className={cn('aspect-video w-full object-cover', status === 'ready' ? 'block' : 'hidden')}
        />

        {status !== 'ready' && (
          <div className="flex aspect-video w-full items-center justify-center">
            {status === 'error' ? (
              <Camera className="h-10 w-10 text-muted-foreground" aria-hidden />
            ) : (
              <Spinner className="h-8 w-8 text-muted-foreground" />
            )}
          </div>
        )}
      </div>

      {status === 'error' && (
        <Alert variant="destructive">
          <AlertDescription>{t('gen.nocamera.message', 'Could not start your camera')}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col gap-2 sm:flex-row">
        <ActionButton
          intent="primary"
          type="button"
          onClick={capture}
          disabled={status !== 'ready'}
          className="w-full sm:w-auto">
          <Camera className="mr-2 h-4 w-4" />
          {t('gen.take.photo.label', 'Take Photo')}
        </ActionButton>
        <ActionButton type="button" intent="dismiss" onClick={handleClose} className="w-full sm:w-auto">
          <X className="mr-2 h-4 w-4" />
          {t('gen.cancel.label', 'Cancel')}
        </ActionButton>
      </div>
    </div>
  );
}
